import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void
          renderButton: (element: HTMLElement, config: any) => void
        }
      }
    }
  }
}

const GOOGLE_CLIENT_ID = '235435667036-3o6cjs45qg488hi8s3i9shoq3c611smn.apps.googleusercontent.com'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login, loginWithGoogle } = useStore()
  const navigate = useNavigate()
  const googleBtnRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Initialize Google Sign-In when the script loads
    const initGoogle = () => {
      if (window.google && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        })
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: googleBtnRef.current.offsetWidth,
          text: 'signin_with',
          shape: 'rectangular',
        })
      }
    }

    // Script might already be loaded
    if (window.google) {
      initGoogle()
    } else {
      // Wait for script to load
      const interval = setInterval(() => {
        if (window.google) {
          clearInterval(interval)
          initGoogle()
        }
      }, 100)
      return () => clearInterval(interval)
    }
  }, [])

  const handleGoogleCallback = async (response: any) => {
    setError('')
    setLoading(true)
    try {
      await loginWithGoogle(response.credential)
      {
        const dest = sessionStorage.getItem('scribey_post_login')
        sessionStorage.removeItem('scribey_post_login')
        navigate(dest || '/')
      }
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      {
        const dest = sessionStorage.getItem('scribey_post_login')
        sessionStorage.removeItem('scribey_post_login')
        navigate(dest || '/')
      }
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brand-500">Dr. Scribey</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">AI Medical Scribe</p>
        </div>

        {/* Form */}
        <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-6 space-y-4 shadow-xl border border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Sign In</h2>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg p-3">
              {error}
            </div>
          )}

          {/* Google Sign-In Button (rendered by Google) */}
          <div ref={googleBtnRef} className="w-full flex justify-center" />

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-500">Or continue with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex justify-end -mt-2">
              <Link to="/forgot-password" className="text-xs text-brand-400 hover:text-brand-300">Forgot password?</Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-600 hover:bg-brand-500 disabled:bg-brand-800 text-white font-medium py-2.5 rounded-lg transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-brand-400 hover:text-brand-300">
              Create one
            </Link>
          </p>

          <p className="text-center text-sm">
            <Link to="/station-login" className="text-brand-400 hover:text-brand-300">
              Sign in on a shared computer
            </Link>
          </p>

          {/* Legal links */}
          <div className="flex justify-center gap-3 pt-2 text-xs text-gray-400">
            <a href="/security" className="hover:text-gray-600">Security</a>
            <span>·</span>
            <a href="/terms" className="hover:text-gray-600">Terms</a>
            <span>·</span>
            <a href="/privacy" className="hover:text-gray-600">Privacy</a>
          </div>
        </div>
      </div>
    </div>
  )
}
